/**
 * Single-shot pub/sub for "client version is out of sync with backend" events.
 *
 * The backend signals a stale client by responding with
 * `X-Skymap-Client-Stale: 1` (see SK-7015). The shared axios response
 * interceptor detects that header and calls {@link notifyVersionMismatch}.
 * A React component listens via {@link subscribeToVersionMismatch} and renders
 * the user-facing "new version available — please reload" notice.
 *
 * Backend may keep responding with the stale header on every subsequent
 * request until the page reloads, so listeners are notified at most once per
 * page lifetime to avoid spamming the UI.
 */
type VersionMismatchListener = () => void;

const listeners = new Set<VersionMismatchListener>();
let alreadyNotified = false;

export const subscribeToVersionMismatch = (listener: VersionMismatchListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const notifyVersionMismatch = (): void => {
  if (alreadyNotified) {
    return;
  }
  alreadyNotified = true;
  for (const listener of listeners) {
    listener();
  }
};

/**
 * Reset the "already notified" flag. Intended for tests only — production code
 * should never need to re-arm the notification.
 */
export const __resetVersionMismatchForTests = (): void => {
  alreadyNotified = false;
  listeners.clear();
};
